import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import {
  Clock,
  Video,
  Building2,
  Users,
  Pencil,
  Trash2,
  FileText,
  Briefcase,
  AlertTriangle,
  Mail,
  MailCheck,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@aaas/shared';
import { useDeleteCalendarEventMutation } from '../hooks/useCalendarQueries';
import type { CalendarEvent, CalendarAttendeeRole } from '../types';
import { EVENT_STATUS_CONFIG, ATTENDEE_ROLE_CONFIG } from '../types/calendar';
import { SendEmailDialog } from './SendEmailDialog';

interface CalendarEventDetailDialogProps {
  event: CalendarEvent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (event: CalendarEvent) => void;
  readOnly?: boolean;
}

export function CalendarEventDetailDialog({
  event,
  open,
  onOpenChange,
  onEdit,
  readOnly = false,
}: CalendarEventDetailDialogProps) {
  const { t } = useTranslation('ats');
  const queryClient = useQueryClient();
  const deleteMutation = useDeleteCalendarEventMutation();
  const [sendOpen, setSendOpen] = useState(false);

  const canSendEmail = !!event?.cv_id;
  const templateEmailSentAt = event?.template_email_sent_at ?? null;

  if (!event) return null;

  const typeLabel = event.event_type_name || t('calendar.eventDetail.unknownType');
  const statusConfig = EVENT_STATUS_CONFIG[event.status] ?? {
    label: event.status,
    color: 'bg-gray-100 text-gray-700',
  };

  const handleDelete = async () => {
    await deleteMutation.mutateAsync(event.id);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-lg">{event.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Badges */}
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
              {typeLabel}
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
              {statusConfig.label}
            </span>
          </div>

          {/* Time */}
          <div className="flex items-start gap-3 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div>
              <div>{format(parseISO(event.start_time), 'PPP')}</div>
              <div className="text-muted-foreground">
                {format(parseISO(event.start_time), 'p')} -{' '}
                {format(parseISO(event.end_time), 'p')}
              </div>
            </div>
          </div>

          {/* Location */}
          {event.location && (
            <div className="flex items-start gap-3 text-sm">
              {event.location_type === 'online' ? (
                <Video className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              ) : (
                <Building2 className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              )}
              <div>
                {event.location_type === 'online' && event.location ? (() => {
                  try {
                    const url = new URL(event.location);
                    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
                      return <span>{event.location}</span>;
                    }
                    // Guard against IDN homograph attacks: if the hostname
                    // contains any non-ASCII character, the label may be
                    // visually indistinguishable from a legitimate domain
                    // (e.g. Cyrillic `р` vs Latin `p`). Render non-clickable
                    // with a warning and show the Punycode form when available
                    // so a human can sanity-check the true hostname.
                    const rawHostname = event.location
                      .replace(/^https?:\/\//i, '')
                      .split(/[/?#]/)[0];
                    if (/[^\x00-\x7F]/.test(rawHostname)) {
                      return (
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-1.5 text-amber-700">
                            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="text-xs font-medium">
                              {t('calendar.eventDetail.linkWarning')}
                            </span>
                          </div>
                          <div className="break-all">{event.location}</div>
                          <div className="text-xs text-muted-foreground break-all">
                            {t('calendar.eventDetail.punycode', { host: url.hostname })}
                          </div>
                        </div>
                      );
                    }
                    return (
                      <a
                        href={event.location}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {event.location}
                      </a>
                    );
                  } catch {
                    // not a valid URL
                  }
                  return <span>{event.location}</span>;
                })() : (
                  <span>{event.location}</span>
                )}
              </div>
            </div>
          )}

          {/* CV Info */}
          {event.cv_name && (
            <div className="flex items-start gap-3 text-sm">
              <FileText className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-xs text-muted-foreground">{t('calendar.eventDetail.candidate')}</span>
                <div>
                  <a
                    href={`/ats/cvs/${event.cv_id}`}
                    className="text-primary hover:underline"
                  >
                    {event.cv_name}
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Job Info */}
          {(event.job_code || event.job_position_name) && (
            <div className="flex items-start gap-3 text-sm">
              <Briefcase className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-xs text-muted-foreground">{t('calendar.eventDetail.job')}</span>
                <div>
                  <a
                    href={`/ats/jobs/${event.job_id}`}
                    className="text-primary hover:underline"
                  >
                    {event.job_code}{event.job_position_name ? ` - ${event.job_position_name}` : ''}
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Description */}
          {event.description && (
            <div className="text-sm">
              <p className="text-muted-foreground whitespace-pre-wrap">{event.description}</p>
            </div>
          )}

          {/* Attendees grouped by role */}
          {event.attendees.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span>{t('calendar.eventDetail.picSection')}</span>
              </div>
              {(['interviewer', 'hr', 'attendee'] as CalendarAttendeeRole[]).map((role) => {
                const roleAttendees = event.attendees.filter(
                  (a) => (a.role ?? 'attendee') === role
                );
                if (roleAttendees.length === 0) return null;
                const config = ATTENDEE_ROLE_CONFIG[role];
                return (
                  <div key={role} className="pl-6 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                        {config.label}
                      </span>
                      <span className="text-xs text-muted-foreground">({roleAttendees.length})</span>
                    </div>
                    {roleAttendees.map((attendee) => (
                      <div key={attendee.id} className="text-sm pl-2">
                        <span>{attendee.display_name}</span>
                        {attendee.email && (
                          <span className="text-muted-foreground ml-1">({attendee.email})</span>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          {/* Templated-email status */}
          {canSendEmail && (
            <div
              className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm ${
                templateEmailSentAt
                  ? 'bg-green-50 text-green-800'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {templateEmailSentAt ? (
                <>
                  <MailCheck className="h-4 w-4 flex-shrink-0" />
                  <span>
                    {t('calendar.eventDetail.templatedEmailSent', { at: format(parseISO(templateEmailSentAt), 'PPp') })}
                  </span>
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 flex-shrink-0" />
                  <span>{t('calendar.eventDetail.templatedEmailNone')}</span>
                </>
              )}
            </div>
          )}

          {/* Actions */}
          {!readOnly && (
            <div className="flex items-center justify-end gap-2 pt-2 border-t">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="text-destructive">
                    <Trash2 className="h-4 w-4 mr-1" />
                    {t('calendar.eventDetail.delete')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('calendar.eventDetail.deleteTitle')}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('calendar.eventDetail.deleteBody', { title: event.title })}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('calendar.eventDetail.deleteCancel')}</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete}>{t('calendar.eventDetail.deleteConfirm')}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {canSendEmail && (
                <Button variant="outline" size="sm" onClick={() => setSendOpen(true)}>
                  <Mail className="h-4 w-4 mr-1" />
                  {templateEmailSentAt ? t('calendar.eventDetail.resendTemplate') : t('calendar.eventDetail.sendTemplate')}
                </Button>
              )}

              {onEdit && (
                <Button size="sm" onClick={() => onEdit(event)}>
                  <Pencil className="h-4 w-4 mr-1" />
                  {t('calendar.eventDetail.edit')}
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
      {canSendEmail && event.cv_id && (
        <SendEmailDialog
          open={sendOpen}
          onClose={() => setSendOpen(false)}
          cvIds={[event.cv_id]}
          jobId={event.job_id ?? undefined}
          calendarEventId={event.id}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
          }}
        />
      )}
    </Dialog>
  );
}
